// The record's By product ledger (pipeline RC4, stage 3; brief of 7 Oct section 4 item 7, drawing a6-account-file.html
// "By product"): one row per product line the company takes, with its forecast (billed in the note), the potential on
// its open deals, its agreements on the register, its use read against its own pattern, and the incidents that name it.
// Every figure is the one company read's (RC1) or the incident log's own count; nothing is estimated here. The products
// it does not take are named under the table.
import { gbp, plateDay, whose, type AgreementInForce, type CompanyRead, type ProductReading } from './company-read';

/** The product lines the record names, in the order the drawing gives them. */
export const LEDGER_PRODUCTS = ['OneVideo', 'OneTrade', 'One Data', 'One Retail', 'Bettor Engagement'] as const;

/**
 * The product line a name is sold as: a deal's product or family ("OneVideo and One OTT", "One Trade"), an incident's
 * product, or the register's words. The read's own money lines and agreements already carry it (company-read.ts
 * VERTICALS, verticalOfRegister); this reads the other names the same way. A name it does not know keeps its own words.
 */
export function productOf(name: string | null): string | null {
  const raw = (name ?? '').trim();
  const p = raw.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!p) return null;
  if (p.startsWith('onevideo') || /oneplatform|onemap|modus|sportsbooksservices|contentlicen/.test(p)) return 'OneVideo';
  if (/onetrade|inplay|oneott|^ott/.test(p)) return 'OneTrade';
  if (/onedata/.test(p)) return 'One Data';
  if (/oneretail|^retail/.test(p)) return 'One Retail';
  if (/bettorengagement/.test(p)) return 'Bettor Engagement';
  if (/rightsholder|ingest/.test(p)) return 'Rights holder services';
  if (/padel/.test(p)) return 'Premier Padel';
  return raw;
}

/** The section a product's row opens, where the record has one (the verdict rows open the same). */
export const productAnchor = (product: string): string | null =>
  product === 'OneVideo' ? 'a-onevideo' : product === 'OneTrade' ? 'a-onetrade' : null;

export interface LedgerCell { main: string; note: string | null }
export interface LedgerRow {
  product: string;
  anchor: string | null;
  forecast: LedgerCell;
  potential: LedgerCell;
  agreements: LedgerCell;
  use: LedgerCell & { chip: boolean };
  service: LedgerCell;
}
export interface Ledger { sub: string; rows: LedgerRow[]; notTaken: string[] }

/** Incidents in the log by product, as the API counts them over the year (records.ts companyOperations). */
export interface ServiceByProduct { product: string | null; last12: number; unresolved: number }

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const monthWords = (m: string): string => { const [y, mm] = m.split('-').map(Number); return `${MONTHS[(mm ?? 1) - 1]} ${y}`; };
const sumOf = (xs: number[]): number => xs.reduce((a, b) => a + b, 0);

function agreementName(a: AgreementInForce): string {
  return a.competition ?? a.product ?? 'An agreement';
}

function useCell(product: string, readings: CompanyRead['readings']): LedgerCell & { chip: boolean } {
  const r: ProductReading | null = product === 'OneTrade' ? readings.oneTrade : product === 'OneVideo' ? readings.oneVideo : null;
  if (r?.state === 'read') return { main: r.chip, note: r.sentence, chip: true };
  if (r?.state === 'none') return { main: 'No reading yet', note: r.words, chip: false };
  return { main: 'Not read yet', note: null, chip: false };
}

/**
 * The ledger from the read. A product is a row when finance forecasts or billed money on it, an agreement on the
 * register is for it, an open deal on it carries a figure, or its use has been read; rows run by forecast, then billed.
 * `service` is the incident log's count by product, or null where the record has no log.
 */
export function ledgerOf(read: CompanyRead, service: ServiceByProduct[] | null): Ledger {
  const name = read.company.name;
  const { billed, forecast, potential } = read.money;
  const money = read.money.available;
  const inForce = read.agreements.inForce;
  const seen = new Set<string>();
  const add = (p: string | null) => { if (p && p !== 'Other') seen.add(p); };
  if (money) { forecast.lines.forEach((l) => add(l.vertical)); billed.lines.forEach((l) => add(l.vertical)); }
  inForce.forEach((a) => add(a.vertical));
  potential.byProduct.forEach((p) => add(productOf(p.product)));
  for (const p of ['OneVideo', 'OneTrade'] as const) if (useCell(p, read.readings).chip) add(p);

  const rows: LedgerRow[] = [...seen].map((product) => {
    const f = money ? forecast.lines.filter((l) => l.vertical === product) : [];
    const b = money ? billed.lines.filter((l) => l.vertical === product) : [];
    const fTotal = f.length ? sumOf(f.map((l) => l.amount)) : null;
    const bTotal = b.length ? sumOf(b.map((l) => l.amount)) : null;
    const pot = potential.byProduct.filter((x) => productOf(x.product) === product);
    const own = inForce.filter((a) => a.vertical === product);
    const svc = service ? service.filter((s) => productOf(s.product) === product) : null;
    const s12 = svc ? sumOf(svc.map((s) => s.last12)) : 0;
    const sOpen = svc ? sumOf(svc.map((s) => s.unresolved)) : 0;
    const listed = own.slice(0, 3).map((a) => (a.endsOn ? `${agreementName(a)} to ${plateDay(a.endsOn)}` : `${agreementName(a)}, no end date`));
    return {
      product,
      anchor: productAnchor(product),
      forecast: !money
        ? { main: 'No billing linked', note: null }
        : {
          main: fTotal !== null ? gbp(fTotal) : 'None forecast',
          note: bTotal !== null ? `billed ${gbp(bTotal)} in the twelve months before` : fTotal !== null ? 'nothing billed in the twelve months before' : null,
        },
      potential: pot.length ? { main: gbp(sumOf(pot.map((x) => x.amount))), note: 'on its open deals' } : { main: 'None on a deal', note: null },
      agreements: own.length
        ? { main: `${own.length} of ${whose(name)} ${inForce.length}`, note: `${listed.join('; ')}${own.length > 3 ? `; and ${own.length - 3} more` : ''}` }
        : { main: 'None on the register', note: null },
      use: useCell(product, read.readings),
      service: svc === null
        ? { main: 'No log here yet', note: null }
        : s12 > 0
          ? { main: `${s12} ${s12 === 1 ? 'incident' : 'incidents'} in the last year`, note: sOpen ? `${sOpen} never closed in the log` : null }
          : { main: `No incident names ${name}`, note: null },
    };
  });
  const fOf = (r: LedgerRow) => sumOf(money ? forecast.lines.filter((l) => l.vertical === r.product).map((l) => l.amount) : []);
  const bOf = (r: LedgerRow) => sumOf(money ? billed.lines.filter((l) => l.vertical === r.product).map((l) => l.amount) : []);
  rows.sort((a, b) => fOf(b) - fOf(a) || bOf(b) - bOf(a) || a.product.localeCompare(b.product));

  const fw = forecast.window;
  const sub = money && fw
    ? `money forecast for ${monthWords(fw.from)} to ${monthWords(fw.to)}, and billed in the twelve months before`
    : 'no billing is linked to this company';
  return { sub, rows, notTaken: LEDGER_PRODUCTS.filter((p) => !seen.has(p)) };
}
